import { Common, color } from '@stockbit/ui';
import styled, { css, CSSProperties } from 'styled-components';
import { Table } from 'antd';

const { Flex, Text, Modal, Button } = Common;

const cellType = {
  up: color.primary,
  down: color.sunsetOrange,
  neutral: color.suvaGrey,
  default: color.highempahis,
};

export const headerHeight = 40;

const HeaderContainer = styled(Flex).attrs({
  align: 'center',
  justify: 'space-between',
  px: '12px',
})`
  position: relative;
  background: ${color.aliceBlue};
  height: ${headerHeight}px;
  cursor: pointer;
  /* border-bottom: 1px solid ${color.gainsboro}; */
`;

const Td = styled(Text).attrs((props) => ({
  height: '1.15',
  padding: '0',
  size: '13px',
  type: props.type || 'default',
}))`
  cursor: pointer;
  font-variant: normal;
  -webkit-font-feature-settings: normal;
  font-feature-settings: normal;
  word-break: keep-all;

  ${({ type }) =>
    css`
      color: ${cellType[type]};
    `}
`;

const Th = styled(Td).attrs({
  family: 'bold',
  size: '13px',
})``;

interface EmptyPriceAlertProps {
  maxHeight?: CSSProperties['maxHeight'];
}

const EmptyPriceAlert = styled.div<EmptyPriceAlertProps>`
  background-color: #ffffff;
  text-align: center;
  height: calc(100% - ${headerHeight}px);
  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight}`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: auto;

  > .add-alert {
    color: #00ab6b;
    margin: 10px 0;
    width: 80%;
    border: solid 1px #dadada;
    padding: 5px 15px;
    display: inline-block;
    border-radius: 2px;
  }

  > .add-alert .icon-toolbar_plus {
    position: relative;
    top: 5px;
    right: 6px;
    font-size: 12px;
    margin-left: 5px;
    vertical-align: middle;
  }
`;

const PriceAlertTd = styled(Td)`
  span.active {
    background: #00ab6b;
    height: 5px;
    width: 5px;
    display: inline-block;
    overflow: hidden;
    border-radius: 50%;
    color: transparent;
    margin-right: 8px;
    vertical-align: middle;
  }
  span.pending {
    background: #979134;
    height: 5px;
    width: 5px;
    display: inline-block;
    overflow: hidden;
    border-radius: 50%;
    color: transparent;
    margin-right: 8px;
    vertical-align: middle;
  }
`;

const ModalPriceAlert = styled(Modal).attrs({ width: '400px' })`
  width: 400px;
  border-radius: 20px;

  .ant-modal-content {
    border-radius: 3px;
    padding: 15px;
  }

  .ant-modal-title {
    font-size: 18px;
    height: 40px;
    text-align: center;
  }

  .ant-modal-footer,
  .ant-modal-header {
    border-radius: 10px;
  }

  .ant-modal-header {
    font-family: var(--bold-font);
    border-bottom-width: 0;
  }

  .ant-modal-footer {
    margin-top: 20px;
    border-top-width: 0;
  }

  .pa-condition {
    width: calc(100% - 10px);
    float: left;
  }

  .pa-condition select {
    width: 100%;
    height: 30px;
    border: solid 1px #dadada;
    padding: 0 5px;
  }

  .pa-value {
    width: calc(96% - 10px);
    margin-left: 20px;
    float: left;
  }

  .pa-value input {
    height: 30px;
    border: solid 1px #dadada;
    min-width: 100%;
    padding: 0 5px;
  }

  .pa-value:after {
    clear: both;
    display: block;
    content: '';
  }

  .pa-description {
    margin-top: 10px;
  }

  .pa-description:before {
    clear: both;
    display: block;
    content: '';
  }

  .pa-description textarea {
    width: 100%;
    border: solid 1px #dadada;
    padding: 5px;
  }

  label {
    font-family: var(--regular-font);
  }

  & .ant-modal-close-x {
    color: rgba(0, 0, 0, 0.43);
    font-size: 14px;

    > span {
      vertical-align: baseline;
    }
  }
`;

interface TableLogProps {
  alignSorter?: 'start' | 'end';
}

const TableLog = styled(Table)<TableLogProps>`
  padding: 10px 12px;

  .ant-table-column-sorters-with-tooltip,
  .ant-table-column-sorters {
    display: flex;
    justify-content: ${({ alignSorter }) =>
      alignSorter === 'start' ? 'flex-start' : 'flex-end'};
    padding: 0;

    .ant-table-column-sorter {
      display: flex;
      margin-top: 0;
    }
  }

  & .ant-table {
    thead {
      th {
        background-color: transparent;
        padding: 6px 5px;
      }

      th:first-of-type {
        padding-left: 0;
      }

      th:last-of-type {
        padding-right: 0;
      }
    }

    tbody {
      td {
        overflow-wrap: normal;
        padding: 6px 5px;
      }

      td:first-of-type {
        padding-left: 0;
      }

      td:last-of-type {
        padding-right: 0;
      }
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      border-bottom: 1px solid ${color.aliceBlue};
    }
  }
`;

const LabelForm = styled.label`
  font-size: 12px;
`;

const CustomButton = styled(Button)`
  font-size: 12px;
  line-height: 1.5;
  padding: 7px 15px 8px;

  ${(props) =>
    props.$cancel &&
    css`
      background-color: #f7f7f7;
    `}
`;

export {
  HeaderContainer,
  Td,
  Th,
  EmptyPriceAlert,
  PriceAlertTd,
  ModalPriceAlert,
  TableLog,
  LabelForm,
  CustomButton,
};
